import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SuperAdminState } from './types';
import {
  fetchSystemRoles,
  createSystemRole,
  updateSystemRole,
  deleteSystemRole,
  fetchSystemModules,
  createSystemModule,
  updateSystemModule,
  deleteSystemModule,
  fetchRoleModuleMappings,
  createRoleModuleMapping,
  updateRoleModuleMapping,
  deleteRoleModuleMapping,
  fetchUserRoleMappings,
  createUserWithMapping,
  deleteUserRoleMapping,
  updateUserRoleMapping,
  fetchSuperAdminDocumentTypes,
  createSuperAdminDocumentType,
  updateSuperAdminDocumentType,
  deleteSuperAdminDocumentType,
  fetchSuperAdminSignupFields,
  createSuperAdminSignupField,
  updateSuperAdminSignupField,
  deleteSuperAdminSignupField,
  updateUserStatus
} from './actions';


const initialState: SuperAdminState = {
  roles: [],
  rolesLoading: false,
  rolesError: null,

  modules: [],
  modulesLoading: false,
  modulesError: null,

  mappings: [],
  mappingsLoading: false,
  mappingsError: null,

  userMappings: [],
  userMappingsLoading: false,
  userMappingsError: null,

  // Action States
  createRoleLoading: false,
  createRoleSuccess: false,
  createRoleError: null,

  updateRoleLoading: false,
  updateRoleSuccess: false,
  updateRoleError: null,

  deleteRoleLoading: false,
  deleteRoleSuccess: false,
  deleteRoleError: null,

  createModuleLoading: false,
  createModuleSuccess: false,
  createModuleError: null,

  updateModuleLoading: false,
  updateModuleSuccess: false,
  updateModuleError: null,

  deleteModuleLoading: false,
  deleteModuleSuccess: false,
  deleteModuleError: null,

  createMappingLoading: false,
  createMappingSuccess: false,
  createMappingError: null,

  updateMappingLoading: false,
  updateMappingSuccess: false,
  updateMappingError: null,

  deleteMappingLoading: false,
  deleteMappingSuccess: false,
  deleteMappingError: null,

  createUserMappingLoading: false,
  createUserMappingSuccess: false,
  createUserMappingError: null,

  deleteUserMappingLoading: false,
  deleteUserMappingSuccess: false,
  deleteUserMappingError: null,

  updateUserMappingLoading: false,
  updateUserMappingSuccess: false,
  updateUserMappingError: null,

  documentTypes: [],
  documentTypesCount: 0,
  documentTypesLoading: false,
  documentTypesError: null,

  createDocumentTypeLoading: false,
  createDocumentTypeSuccess: false,
  createDocumentTypeError: null,

  updateDocumentTypeLoading: false,
  updateDocumentTypeSuccess: false,
  updateDocumentTypeError: null,

  deleteDocumentTypeLoading: false,
  deleteDocumentTypeSuccess: false,
  deleteDocumentTypeError: null,

  signupFields: [],
  signupFieldsCount: 0,
  signupFieldsLoading: false,
  signupFieldsError: null,

  createSignupFieldLoading: false,
  createSignupFieldSuccess: false,
  createSignupFieldError: null,

  updateSignupFieldLoading: false,
  updateSignupFieldSuccess: false,
  updateSignupFieldError: null,

  deleteSignupFieldLoading: false,
  deleteSignupFieldSuccess: false,
  deleteSignupFieldError: null,

  actionMessage: null,
};

const superAdminSlice = createSlice({
  name: 'superadmin',
  initialState,
  reducers: {
    resetSuperAdminActionState: (state) => {
      // Reset all success/error states
      state.createRoleSuccess = false;
      state.createRoleError = null;
      state.updateRoleSuccess = false;
      state.updateRoleError = null;
      state.deleteRoleSuccess = false;
      state.deleteRoleError = null;
      
      state.createModuleSuccess = false;
      state.createModuleError = null;
      state.updateModuleSuccess = false;
      state.updateModuleError = null;
      state.deleteModuleSuccess = false;
      state.deleteModuleError = null;

      state.createMappingSuccess = false;
      state.createMappingError = null;
      state.updateMappingSuccess = false;
      state.updateMappingError = null;
      state.deleteMappingSuccess = false;
      state.deleteMappingError = null;

      state.createUserMappingSuccess = false;
      state.createUserMappingError = null;
      state.deleteUserMappingSuccess = false;
      state.deleteUserMappingError = null;
      state.updateUserMappingSuccess = false;
      state.updateUserMappingError = null;

      state.createDocumentTypeSuccess = false;
      state.createDocumentTypeError = null;
      state.updateDocumentTypeSuccess = false;
      state.updateDocumentTypeError = null;
      state.deleteDocumentTypeSuccess = false;
      state.deleteDocumentTypeError = null;

      state.createSignupFieldSuccess = false;
      state.createSignupFieldError = null;
      state.updateSignupFieldSuccess = false;
      state.updateSignupFieldError = null;
      state.deleteSignupFieldSuccess = false;
      state.deleteSignupFieldError = null;

      state.actionMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Roles Fetch
    builder
      .addCase(fetchSystemRoles.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(fetchSystemRoles.fulfilled, (state, action: PayloadAction<any>) => {
        state.rolesLoading = false;
        const data = action.payload.data || action.payload;
        state.roles = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      })
      .addCase(fetchSystemRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload as string;
      });

    // Modules Fetch
    builder
      .addCase(fetchSystemModules.pending, (state) => {
        state.modulesLoading = true;
      })
      .addCase(fetchSystemModules.fulfilled, (state, action: PayloadAction<any>) => {
        state.modulesLoading = false;
        const data = action.payload.data || action.payload;
        state.modules = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      })
      .addCase(fetchSystemModules.rejected, (state, action) => {
        state.modulesLoading = false;
        state.modulesError = action.payload as string;
      });

    // Mappings Fetch
    builder
      .addCase(fetchRoleModuleMappings.pending, (state) => {
        state.mappingsLoading = true;
      })
      .addCase(fetchRoleModuleMappings.fulfilled, (state, action: PayloadAction<any>) => {
        state.mappingsLoading = false;
        const data = action.payload.data || action.payload;
        state.mappings = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      })
      .addCase(fetchRoleModuleMappings.rejected, (state, action) => {
        state.mappingsLoading = false;
        state.mappingsError = action.payload as string;
      });

    // User Mappings Fetch
    builder
      .addCase(fetchUserRoleMappings.pending, (state) => {
        state.userMappingsLoading = true;
      })
      .addCase(fetchUserRoleMappings.fulfilled, (state, action: PayloadAction<any>) => {
        state.userMappingsLoading = false;
        const data = action.payload.data || action.payload;
        state.userMappings = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      })
      .addCase(fetchUserRoleMappings.rejected, (state, action) => {
        state.userMappingsLoading = false;
        state.userMappingsError = action.payload as string;
      });

    // Role Actions
    builder
      .addCase(createSystemRole.pending, (state) => { state.createRoleLoading = true; state.createRoleSuccess = false; state.createRoleError = null; })
      .addCase(createSystemRole.fulfilled, (state, action) => { state.createRoleLoading = false; state.createRoleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createSystemRole.rejected, (state, action) => { state.createRoleLoading = false; state.createRoleError = action.payload as string; })

      .addCase(updateSystemRole.pending, (state) => { state.updateRoleLoading = true; state.updateRoleSuccess = false; state.updateRoleError = null; })
      .addCase(updateSystemRole.fulfilled, (state, action) => { state.updateRoleLoading = false; state.updateRoleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateSystemRole.rejected, (state, action) => { state.updateRoleLoading = false; state.updateRoleError = action.payload as string; })

      .addCase(deleteSystemRole.pending, (state) => { state.deleteRoleLoading = true; state.deleteRoleSuccess = false; state.deleteRoleError = null; })
      .addCase(deleteSystemRole.fulfilled, (state, action) => { state.deleteRoleLoading = false; state.deleteRoleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteSystemRole.rejected, (state, action) => { state.deleteRoleLoading = false; state.deleteRoleError = action.payload as string; });

    // Module Actions
    builder
      .addCase(createSystemModule.pending, (state) => { state.createModuleLoading = true; state.createModuleSuccess = false; state.createModuleError = null; })
      .addCase(createSystemModule.fulfilled, (state, action) => { state.createModuleLoading = false; state.createModuleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createSystemModule.rejected, (state, action) => { state.createModuleLoading = false; state.createModuleError = action.payload as string; })

      .addCase(updateSystemModule.pending, (state) => { state.updateModuleLoading = true; state.updateModuleSuccess = false; state.updateModuleError = null; })
      .addCase(updateSystemModule.fulfilled, (state, action) => { state.updateModuleLoading = false; state.updateModuleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateSystemModule.rejected, (state, action) => { state.updateModuleLoading = false; state.updateModuleError = action.payload as string; })

      .addCase(deleteSystemModule.pending, (state) => { state.deleteModuleLoading = true; state.deleteModuleSuccess = false; state.deleteModuleError = null; })
      .addCase(deleteSystemModule.fulfilled, (state, action) => { state.deleteModuleLoading = false; state.deleteModuleSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteSystemModule.rejected, (state, action) => { state.deleteModuleLoading = false; state.deleteModuleError = action.payload as string; });

    // Mapping Actions
    builder
      .addCase(createRoleModuleMapping.pending, (state) => { state.createMappingLoading = true; state.createMappingSuccess = false; state.createMappingError = null; })
      .addCase(createRoleModuleMapping.fulfilled, (state, action) => { state.createMappingLoading = false; state.createMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createRoleModuleMapping.rejected, (state, action) => { state.createMappingLoading = false; state.createMappingError = action.payload as string; })

      .addCase(updateRoleModuleMapping.pending, (state) => { state.updateMappingLoading = true; state.updateMappingSuccess = false; state.updateMappingError = null; })
      .addCase(updateRoleModuleMapping.fulfilled, (state, action) => { state.updateMappingLoading = false; state.updateMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateRoleModuleMapping.rejected, (state, action) => { state.updateMappingLoading = false; state.updateMappingError = action.payload as string; })

      .addCase(deleteRoleModuleMapping.pending, (state) => { state.deleteMappingLoading = true; state.deleteMappingSuccess = false; state.deleteMappingError = null; })
      .addCase(deleteRoleModuleMapping.fulfilled, (state, action) => { state.deleteMappingLoading = false; state.deleteMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteRoleModuleMapping.rejected, (state, action) => { state.deleteMappingLoading = false; state.deleteMappingError = action.payload as string; });

    // User Mapping Actions
    builder
      .addCase(createUserWithMapping.pending, (state) => { state.createUserMappingLoading = true; state.createUserMappingSuccess = false; state.createUserMappingError = null; })
      .addCase(createUserWithMapping.fulfilled, (state, action) => { state.createUserMappingLoading = false; state.createUserMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createUserWithMapping.rejected, (state, action) => { state.createUserMappingLoading = false; state.createUserMappingError = action.payload as string; })

      .addCase(deleteUserRoleMapping.pending, (state) => { state.deleteUserMappingLoading = true; state.deleteUserMappingSuccess = false; state.deleteUserMappingError = null; })
      .addCase(deleteUserRoleMapping.fulfilled, (state, action) => { state.deleteUserMappingLoading = false; state.deleteUserMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteUserRoleMapping.rejected, (state, action) => { state.deleteUserMappingLoading = false; state.deleteUserMappingError = action.payload as string; })

      .addCase(updateUserRoleMapping.pending, (state) => { state.updateUserMappingLoading = true; state.updateUserMappingSuccess = false; state.updateUserMappingError = null; })
      .addCase(updateUserRoleMapping.fulfilled, (state, action) => { state.updateUserMappingLoading = false; state.updateUserMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateUserRoleMapping.rejected, (state, action) => { state.updateUserMappingLoading = false; state.updateUserMappingError = action.payload as string; })

      .addCase(updateUserStatus.pending, (state) => { state.updateUserMappingLoading = true; state.updateUserMappingSuccess = false; state.updateUserMappingError = null; })
      .addCase(updateUserStatus.fulfilled, (state, action) => { state.updateUserMappingLoading = false; state.updateUserMappingSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateUserStatus.rejected, (state, action) => { state.updateUserMappingLoading = false; state.updateUserMappingError = action.payload as string; });


    // Document Types Fetch
    builder
      .addCase(fetchSuperAdminDocumentTypes.pending, (state) => {
        state.documentTypesLoading = true;
      })
      .addCase(fetchSuperAdminDocumentTypes.fulfilled, (state, action: PayloadAction<any>) => {
        state.documentTypesLoading = false;
        const data = action.payload.data || action.payload;
        state.documentTypes = data.results || [];
        state.documentTypesCount = data.count || 0;
      })
      .addCase(fetchSuperAdminDocumentTypes.rejected, (state, action) => {
        state.documentTypesLoading = false;
        state.documentTypesError = action.payload as string;
      });

    // Document Type Actions
    builder
      .addCase(createSuperAdminDocumentType.pending, (state) => { state.createDocumentTypeLoading = true; state.createDocumentTypeSuccess = false; state.createDocumentTypeError = null; })
      .addCase(createSuperAdminDocumentType.fulfilled, (state, action) => { state.createDocumentTypeLoading = false; state.createDocumentTypeSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createSuperAdminDocumentType.rejected, (state, action) => { state.createDocumentTypeLoading = false; state.createDocumentTypeError = action.payload as string; })

      .addCase(updateSuperAdminDocumentType.pending, (state) => { state.updateDocumentTypeLoading = true; state.updateDocumentTypeSuccess = false; state.updateDocumentTypeError = null; })
      .addCase(updateSuperAdminDocumentType.fulfilled, (state, action) => { state.updateDocumentTypeLoading = false; state.updateDocumentTypeSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateSuperAdminDocumentType.rejected, (state, action) => { state.updateDocumentTypeLoading = false; state.updateDocumentTypeError = action.payload as string; })

      .addCase(deleteSuperAdminDocumentType.pending, (state) => { state.deleteDocumentTypeLoading = true; state.deleteDocumentTypeSuccess = false; state.deleteDocumentTypeError = null; })
      .addCase(deleteSuperAdminDocumentType.fulfilled, (state, action) => { state.deleteDocumentTypeLoading = false; state.deleteDocumentTypeSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteSuperAdminDocumentType.rejected, (state, action) => { state.deleteDocumentTypeLoading = false; state.deleteDocumentTypeError = action.payload as string; });

    // Signup Fields Fetch
    builder
      .addCase(fetchSuperAdminSignupFields.pending, (state) => {
        state.signupFieldsLoading = true;
      })
      .addCase(fetchSuperAdminSignupFields.fulfilled, (state, action: PayloadAction<any>) => {
        state.signupFieldsLoading = false;
        const data = action.payload.data || action.payload;
        state.signupFields = data.results || [];
        state.signupFieldsCount = data.count || 0;
      })
      .addCase(fetchSuperAdminSignupFields.rejected, (state, action) => {
        state.signupFieldsLoading = false;
        state.signupFieldsError = action.payload as string;
      });

    // Signup Field Actions
    builder
      .addCase(createSuperAdminSignupField.pending, (state) => { state.createSignupFieldLoading = true; state.createSignupFieldSuccess = false; state.createSignupFieldError = null; })
      .addCase(createSuperAdminSignupField.fulfilled, (state, action) => { state.createSignupFieldLoading = false; state.createSignupFieldSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(createSuperAdminSignupField.rejected, (state, action) => { state.createSignupFieldLoading = false; state.createSignupFieldError = action.payload as string; })

      .addCase(updateSuperAdminSignupField.pending, (state) => { state.updateSignupFieldLoading = true; state.updateSignupFieldSuccess = false; state.updateSignupFieldError = null; })
      .addCase(updateSuperAdminSignupField.fulfilled, (state, action) => { state.updateSignupFieldLoading = false; state.updateSignupFieldSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(updateSuperAdminSignupField.rejected, (state, action) => { state.updateSignupFieldLoading = false; state.updateSignupFieldError = action.payload as string; })

      .addCase(deleteSuperAdminSignupField.pending, (state) => { state.deleteSignupFieldLoading = true; state.deleteSignupFieldSuccess = false; state.deleteSignupFieldError = null; })
      .addCase(deleteSuperAdminSignupField.fulfilled, (state, action) => { state.deleteSignupFieldLoading = false; state.deleteSignupFieldSuccess = true; state.actionMessage = action.payload.message; })
      .addCase(deleteSuperAdminSignupField.rejected, (state, action) => { state.deleteSignupFieldLoading = false; state.deleteSignupFieldError = action.payload as string; });
  },
});


export const { resetSuperAdminActionState } = superAdminSlice.actions;
export default superAdminSlice.reducer;
